import { useEffect, useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, MenuItem, Grid, Typography, Box
} from "@mui/material";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import RemoveIcon from '@mui/icons-material/Remove';
import { api } from "../../services/api";
import { AlertInfo } from "../../components/Alerts/Alert";
import SimpleBackdrop from "../../components/Loading/SimpleBackdrop";
import { UsuarioType } from "../../types/UsuarioType";
import { TarefaPrioridade } from "../../types/TarefaType";

type Props = {
    open: boolean;
    onClose: () => void;
    onSaved: () => void;
};

type FormData = {
    titulo_tarefa: string;
    descricao_tarefa: string;
    data_tarefa: string;
    id_usuario_responsavel: number | string;
    prioridade_tarefa: TarefaPrioridade;
};

const initialForm: FormData = {
    titulo_tarefa: '',
    descricao_tarefa: '',
    data_tarefa: '',
    id_usuario_responsavel: '',
    prioridade_tarefa: 'normal',
};

type PrioridadeOption = {
    value: TarefaPrioridade;
    label: string;
    color: string;
    bgcolor: string;
    icon: React.ReactNode;
};

const prioridadeOptions: PrioridadeOption[] = [
    {
        value: 'urgente',
        label: 'Urgente',
        color: '#c62828',
        bgcolor: '#ffebee',
        icon: <ErrorOutlineIcon fontSize="small" sx={{ color: '#c62828' }} />,
    },
    {
        value: 'prioridade',
        label: 'Prioridade',
        color: '#e65100',
        bgcolor: '#fff3e0',
        icon: <PriorityHighIcon fontSize="small" sx={{ color: '#e65100' }} />,
    },
    {
        value: 'normal',
        label: 'Normal',
        color: '#555',
        bgcolor: '#f5f5f5',
        icon: <RemoveIcon fontSize="small" sx={{ color: '#555' }} />,
    },
];

export default function ModalCriarTarefa({ open, onClose, onSaved }: Props) {
    const [openLoad, setOpenLoad] = useState(false);
    const [form, setForm] = useState<FormData>(initialForm);
    const [usuarios, setUsuarios] = useState<UsuarioType[]>([]);

    async function loadUsuarios() {
        try {
            const response = await api.get(`/usuarios`);
            if (response.data && !response.data.error) {
                setUsuarios(response.data);
            }
        } catch (error) {
            console.log(error);
        }
    }

    useEffect(() => {
        if (open) {
            setForm(initialForm);
            loadUsuarios();
        }
    }, [open]);

    function handleChange(field: keyof FormData, value: string | number) {
        setForm(prev => ({ ...prev, [field]: value }));
    }

    async function handleSave() {
        if (!form.titulo_tarefa.trim())
            return AlertInfo({ icon: 'info', title: 'Informe o título da tarefa' });
        if (!form.id_usuario_responsavel)
            return AlertInfo({ icon: 'info', title: 'Selecione o responsável' });
        if (!form.data_tarefa)
            return AlertInfo({ icon: 'info', title: 'Informe a data da tarefa' });

        setOpenLoad(true);
        try {
            const response = await api.post(`/createtarefa`, form);
            if (response.data.success) {
                AlertInfo({ icon: 'success', title: 'Tarefa criada com sucesso!' });
                onSaved();
                onClose();
            } else {
                AlertInfo({ icon: 'error', title: response.data.message || 'Falha ao criar tarefa' });
            }
        } catch (error) {
            AlertInfo({ icon: 'error', title: 'Falha na conexão' });
        }
        setOpenLoad(false);
    }

    const selectedPrio = prioridadeOptions.find(p => p.value === form.prioridade_tarefa)!;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <SimpleBackdrop open={openLoad} />
            <DialogTitle>
                <Typography variant="h6" fontWeight="bold">Nova Tarefa</Typography>
                <Typography variant="body2" color="text.secondary">
                    Atribua uma tarefa a um usuário do escritório
                </Typography>
            </DialogTitle>

            <DialogContent dividers>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12}>
                        <TextField
                            label="Título da Tarefa *"
                            fullWidth
                            size="small"
                            value={form.titulo_tarefa}
                            onChange={e => handleChange('titulo_tarefa', e.target.value)}
                            inputProps={{ maxLength: 255 }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label="Descrição / Detalhes"
                            fullWidth
                            size="small"
                            multiline
                            rows={3}
                            value={form.descricao_tarefa}
                            onChange={e => handleChange('descricao_tarefa', e.target.value)}
                        />
                    </Grid>

                    {/* Prioridade — destaque visual */}
                    <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" mb={0.8}>
                            Prioridade *
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {prioridadeOptions.map(opt => {
                                const selected = form.prioridade_tarefa === opt.value;
                                return (
                                    <Box
                                        key={opt.value}
                                        onClick={() => handleChange('prioridade_tarefa', opt.value)}
                                        sx={{
                                            flex: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 0.8,
                                            py: 1.2,
                                            px: 1,
                                            borderRadius: 2,
                                            border: selected ? `2px solid ${opt.color}` : '2px solid transparent',
                                            bgcolor: selected ? opt.bgcolor : '#fafafa',
                                            cursor: 'pointer',
                                            transition: 'all 0.15s',
                                            outline: selected ? `none` : '1px solid #e0e0e0',
                                            '&:hover': { bgcolor: opt.bgcolor, outline: `1px solid ${opt.color}` },
                                        }}
                                    >
                                        {opt.icon}
                                        <Typography
                                            variant="body2"
                                            fontWeight={selected ? 'bold' : 'normal'}
                                            sx={{ color: selected ? opt.color : '#555' }}
                                        >
                                            {opt.label}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Responsável *"
                            fullWidth
                            size="small"
                            select
                            value={form.id_usuario_responsavel}
                            onChange={e => handleChange('id_usuario_responsavel', e.target.value)}
                        >
                            <MenuItem value="">Selecione...</MenuItem>
                            {usuarios.map(u => (
                                <MenuItem key={u.id_usuario} value={u.id_usuario}>
                                    {u.nome_usuario}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Data da Tarefa *"
                            fullWidth
                            size="small"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            value={form.data_tarefa}
                            onChange={e => handleChange('data_tarefa', e.target.value)}
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} color="inherit" variant="outlined">
                    Cancelar
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disableElevation
                    sx={{
                        bgcolor: selectedPrio.color,
                        '&:hover': { bgcolor: selectedPrio.color, filter: 'brightness(0.9)' }
                    }}
                >
                    Criar Tarefa
                </Button>
            </DialogActions>
        </Dialog>
    );
}
